import React, { useState, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useAuthStore, useToastStore } from '@/store';
import { accountApi } from '@/lib/api';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import useTranslation from '@/hooks/useTranslation';

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  inputWrapper: {
    gap: 8,
    width: '100%',
  },
  errorContainer: {
    marginBottom: 6,
    paddingVertical: 12,
    paddingLeft: 18,
    paddingRight: 12,
    borderRadius: 6,
    backgroundColor: '#FEE2E2',
    borderWidth: 1,
    borderColor: '#EF4444',
  },
});

export default function UserUpdateEmailForm() {
  const { addToast } = useToastStore(state => state.actions);
  const { t } = useTranslation();
  const userEmail = useAuthStore(state => state.user?.email);
  const updateUserEmail = useAuthStore(state => state.updateUserEmail);

  const [email, setEmail] = useState(userEmail || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [error, setError] = useState('');

  const isEmailDisabled = useMemo(() => {
    return isUpdatingEmail || !email || !currentPassword;
  }, [isUpdatingEmail, email, currentPassword]);

  const handleUpdateEmail = async () => {
    try {
      setError('');
      if (!email) {
        setError(t('forms.email.enterEmail'));
        return;
      }
      if (!currentPassword) {
        setError(t('forms.email.enterPassword'));
        return;
      }
      setIsUpdatingEmail(true);
      const response = await accountApi.changeEmail(userEmail, email, currentPassword);
      if (!response.error) {
        updateUserEmail(email);
        setEmail('');
        setCurrentPassword('');
        addToast(response.message || t('forms.email.updatedSuccess'), 'success');
      }
    } catch (e) {
      setError(e?.message || t('forms.email.updateFailed'));
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.inputWrapper}>
        {error ? (
          <View style={styles.errorContainer}>
            <Text color='danger' size='n'>{error}</Text>
          </View>
        ) : null}

        <FloatingLabelInput
          label={t('forms.email.currentEmail')}
          value={userEmail || ''}
          editable={false}
          disabled={true}
          isFullWidth
        />
        <FloatingLabelInput
          label={t('forms.email.newEmail')}
          value={email}
          onChangeText={setEmail}
          placeholder={t('forms.email.newEmailPlaceholder')}
          autoCapitalize="none"
          autoComplete="email"
          keyboardType="email-address"
          isFullWidth
        />
        <FloatingLabelInput
          label={t('forms.email.currentPassword')}
          value={currentPassword}
          onChangeText={setCurrentPassword}
          placeholder={t('forms.email.currentPassword')}
          secureTextEntry
          isFullWidth
        />
      </View>
      <Button
        text={t('forms.email.updateEmail')}
        variant={isEmailDisabled ? 'secondary' : 'primary'}
        isLoading={isUpdatingEmail}
        isDisabled={isEmailDisabled}
        onPress={handleUpdateEmail}
        style={{ marginTop: 8 }}
      />
    </View>
  );
} 